(() => {
  if (customElements.get('sh-email-capture')) return;

  class SportsHubEmailCapture extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.teaser = this.querySelector('[data-capture-teaser]');
      this.trigger = this.querySelector('[data-capture-open]');
      this.isSupportedLocale = ['es', 'fr', 'de', 'it', 'nb', 'nl', 'pt-pt', 'no', 'pt'].includes((this.dataset.locale || '').replace('_', '-').toLowerCase());
      this.formsRoot = this.isSupportedLocale && this.dataset.review !== 'true' ? this.querySelector('[data-capture-forms]') : null;
      this.backdrop = this.formsRoot ? this.querySelector('[data-capture-backdrop]') : null;
      this.form = this.formsRoot ? null : this.querySelector('form');
      if (!this.dialog || (!this.form && !this.formsRoot)) return;
      this.timer = null;
      this.attempts = 0;
      this.storageKey = 'sh-welcome5-ui-v1';
      this.completionKey = this.dataset.review === 'true' ? 'reviewCompletedAt' : 'completedAt';
      this.preferences = this.readPreferences();
      this.autoShown = false;
      const result = this.form?.querySelector('[data-capture-result]')?.dataset.result;
      if (result === 'success' || result === 'error') this.dataset.state = result;
      if (result === 'success') this.rememberCompletion();
      const options = { signal: this.controller.signal };

      this.trigger?.addEventListener('click', () => this.open(), options);
      this.querySelectorAll('[data-capture-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), options);
      });
      this.querySelector('[data-capture-dismiss]')?.addEventListener('click', () => {
        this.preferences.teaserUntil = this.dismissUntil();
        this.preferences.autoUntil = this.dismissUntil();
        this.writePreferences();
        this.updateTeaser();
        clearTimeout(this.timer);
      }, options);
      this.dialog.addEventListener('cancel', () => {
        this.reportClarityClose();
        this.rememberClose();
      }, options);
      this.dialog.addEventListener('close', () => this.afterDialogClosed(), options);
      if (this.formsRoot) {
        this.acceptedEmails ||= new Set();
        this.formsEmail = '';
        this.formsEmailCommitted = false;
        this.formsSuccessHadAttempt = false;
        this.dataset.visitorIdentification = 'idle';
        for (const type of ['input', 'click', 'keydown']) {
          this.formsRoot.addEventListener(type, (event) => this.captureFormsEmail(event), { ...options, capture: true });
        }
        document.addEventListener('visitorConsentCollected', () => {
          if (!this.visitorConsentAllowed()) {
            this.clearFormsIdentity();
            this.dataset.visitorIdentification = this.dataset.visitorAnalyticsAllowed === 'unknown' || this.dataset.visitorMarketingAllowed === 'unknown' ? 'unavailable' : 'blocked';
          }
        }, options);
        this.visitorConsentAllowed();
        this.prepareVisitorIdentification(false);
        this.backdrop?.addEventListener('click', () => this.close(), options);
        document.addEventListener('keydown', (event) => this.handleFormsKeydown(event), { ...options, capture: true });
        document.addEventListener('focusin', (event) => {
          if (!this.dialog.open || this.syncChallengeVisibility()) return;
          const target = event.composedPath?.()[0] || event.target;
          // hCaptcha may focus its frame immediately before its visible position is applied.
          if (this.isHCaptchaChallenge(target) || this.insideDialog(target)) return;
          this.focusFormsElement(this.formsFocusable()[0]);
        }, { ...options, capture: true });
      }
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      }, options);
      document.addEventListener('visibilitychange', () => {
        clearTimeout(this.timer);
        if (!document.hidden) {
          this.schedule();
          if (this.formsRoot && this.dialog.open) this.watchFormsSuccess();
        }
      }, options);
      document.addEventListener('sh:email-capture:open', () => this.open(), options);
      if (this.isSupportedLocale) {
        window.addEventListener('pageshow', (event) => {
          if (!event.persisted) return;
          this.refreshPreferences();
        }, options);
        window.addEventListener('storage', (event) => {
          if (event.key === this.storageKey) this.refreshPreferences();
        }, options);
      }

      if (this.dataset.review === 'true') {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          if (!this.form.reportValidity()) return;
          this.dataset.state = 'success';
          this.rememberCompletion();
          this.querySelector('[data-capture-success]')?.focus();
        }, options);
      } else if (this.isSupportedLocale && !this.formsRoot) {
        this.acceptedEmails ||= new Set();
        this.visitorBridgeKey = 'sh-welcome5-visitor-v2';
        if (result === 'error') this.clearVisitorBridge();
        // Consume and delete before waiting for privacy initialization or calling Visitor API.
        const successForThisForm = result === 'success' && window.location.hash === '#' + this.form.id;
        const postSuccessEmail = result === 'success' ? this.takeVisitorBridge() : null;
        // Identification is optional: the native POST and captcha remain in control.
        this.form.addEventListener('submit', () => this.identifyVisitorFromForm(), options);
        const captureOptions = { ...options, capture: true };
        this.form.addEventListener('click', (event) => {
          const submitControl = event.target?.closest?.('button[type="submit"], input[type="submit"]');
          if (submitControl?.form === this.form && !submitControl.disabled) this.identifyVisitorFromForm();
        }, captureOptions);
        this.form.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' && !event.isComposing &&
              event.target === this.form.querySelector('input[name="contact[email]"]')) this.identifyVisitorFromForm();
        }, captureOptions);
        document.addEventListener('visitorConsentCollected', () => {
          if (!this.visitorConsentAllowed()) this.clearVisitorBridge();
        }, options);
        this.prepareVisitorIdentification(successForThisForm, postSuccessEmail);
      }

      this.updateTeaser();
      if (this.dataset.state === 'success' || this.dataset.state === 'error' || window.location.hash === '#sh-email-capture') {
        this.open();
      } else if (!window.Shopify?.designMode) {
        this.schedule();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.formsTimer);
      this.challengeObserver?.disconnect();
      if (this.formsRoot) this.clearFormsIdentity();
      if (this.backdrop) this.backdrop.hidden = true;
      if (this.formsRoot) this.dialog?.removeAttribute('aria-modal');
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) {
        if (typeof this.dialog.close === 'function') this.dialog.close();
        else if (this.formsRoot) this.dialog.removeAttribute('open');
      }
      document.documentElement.classList.remove('sh-capture-is-open');
    }

    insideDialog(element) {
      for (let node = element; node; node = node.parentElement || node.getRootNode?.().host) {
        if (node === this.dialog) return true;
      }
      return false;
    }

    captureFormsEmail(event) {
      if (!this.formsRoot || !this.dialog.open || this.dataset.state === 'success' || !['input', 'click', 'keydown'].includes(event.type)) return;
      try {
        const path = event.composedPath?.() || [event.target];
        const submit = event.type === 'click' ? path.find((element) => element.matches?.('button[type="submit"], input[type="submit"]')) : null;
        if (event.type === 'input' && this.formsEmailCommitted) return;
        if (event.type === 'click' && (!submit || submit.disabled)) return;
        if (event.type === 'keydown' && (event.key !== 'Enter' || event.isComposing || event.altKey || event.ctrlKey || event.metaKey)) return;
        const roots = [this.formsRoot];
        for (let index = 0; index < roots.length; index++) {
          for (const element of roots[index].querySelectorAll('*')) if (element.shadowRoot) roots.push(element.shadowRoot);
        }
        const inputs = roots.flatMap((root) => Array.from(root.querySelectorAll('input[id="email"]')))
          .filter((input) => (input.getAttribute('type') || 'text').toLowerCase() === 'text' && !input.disabled && this.elementVisible(input));
        if (inputs.length !== 1) { if (!this.formsEmailCommitted) this.clearFormsEmail(); return; }
        const input = inputs[0];
        if (event.type === 'input' && path[0] !== input) return;
        if (event.type === 'click' && (!input.form || submit.form !== input.form)) return;
        if (event.type === 'keydown') {
          if (path[0] !== input || !input.form || !roots.some((root) => Array.from(root.querySelectorAll('button[type="submit"], input[type="submit"]'))
            .some((button) => !button.disabled && button.form === input.form && this.elementVisible(button)))) return;
        }
        const email = input.value?.trim() || '';
        if (!/^[^\s@]+@[^\s@]+$/.test(email)) { if (!this.formsEmailCommitted) this.clearFormsEmail(); return; }
        this.clearFormsEmail();
        // Once submitted, this snapshot belongs to that attempt; later input/reset events cannot replace it.
        this.formsEmail = email;
        this.formsEmailCommitted = event.type === 'click' || event.type === 'keydown';
        this.formsEmailAt = Date.now();
        this.formsEmailTimer = setTimeout(() => this.clearFormsEmail(), 600000);
      } catch { if (!this.formsEmailCommitted) this.clearFormsEmail(); }
    }

    clearFormsEmail() {
      clearTimeout(this.formsEmailTimer);
      this.formsEmail = '';
      this.formsEmailAt = 0;
      this.formsEmailCommitted = false;
    }

    clearFormsIdentity() {
      this.clearFormsEmail();
      this.formsSuccessHadAttempt = false;
      if (this.cancelPendingFormsIdentification) {
        this.cancelPendingFormsIdentification();
        this.dataset.visitorIdentification = 'unavailable';
      }
      this.acceptedEmails?.clear();
    }

    elementVisible(element, bounds) {
      if (!element?.isConnected) return false;
      const box = element.getBoundingClientRect();
      if (!element.getClientRects().length || box.width <= 0 || box.height <= 0) return false;
      if (bounds && (box.right <= bounds.left || box.left >= bounds.right ||
          box.bottom <= bounds.top || box.top >= bounds.bottom)) return false;
      for (let node = element; node; node = node.parentElement || node.getRootNode?.().host) {
        const style = getComputedStyle(node);
        if (node.hidden || node.inert || node.getAttribute('aria-hidden') === 'true' ||
            (node.tagName === 'DIALOG' && !node.open) || style.display === 'none' ||
            style.visibility === 'hidden' || style.visibility === 'collapse' ||
            style.contentVisibility === 'hidden' || Number(style.opacity) === 0) return false;
      }
      return true;
    }

    isHCaptchaChallenge(element) {
      if (element?.tagName !== 'IFRAME' || this.insideDialog(element)) return false;
      const title = (element.getAttribute('title') || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
      if (!/^(?:hcaptcha[\s-]+(?:challenge|desafio|defi|herausforderung|sfida|utfordring|uitdaging)|(?:challenge|desafio|defi|herausforderung|sfida|utfordring|uitdaging)(?:\s+(?:de|do|di|von|for|voor))?[\s-]+hcaptcha|main content of the hcaptcha challenge)$/i.test(title)) return false;
      try {
        const host = new URL(element.getAttribute('src'), window.location.href).hostname;
        return host === 'hcaptcha.com' || host.endsWith('.hcaptcha.com');
      } catch { return false; }
    }

    externalChallengeVisible() {
      const bounds = { left: 0, top: 0, right: window.innerWidth || Infinity, bottom: window.innerHeight || Infinity };
      return Array.from(document.querySelectorAll('iframe')).some((frame) =>
        this.isHCaptchaChallenge(frame) && this.elementVisible(frame, bounds));
    }

    activePublicElement() {
      let active = document.activeElement;
      while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
      return active;
    }

    formsFocusable() {
      const found = [];
      const visit = (parent) => {
        const assigned = parent.tagName === 'SLOT' ? parent.assignedElements?.({ flatten: true }) : null;
        const children = assigned?.length ? assigned : (parent.shadowRoot || parent).children;
        for (const element of Array.from(children || [])) {
          if (element.matches('a[href], button, input, select, textarea, summary, iframe, [tabindex], [contenteditable="true"]') &&
              element.tabIndex >= 0 && !element.disabled && this.elementVisible(element)) found.push(element);
          visit(element);
        }
      };
      visit(this.dialog);
      return found;
    }

    focusFormsElement(element) {
      if (!element) return;
      element.focus({ preventScroll: true });
      const box = element.getBoundingClientRect();
      const dialogBox = this.dialog.getBoundingClientRect();
      // Reveal wrapped keyboard focus by scrolling this dialog only, never the storefront page.
      if (box.top < dialogBox.top) this.dialog.scrollTop -= dialogBox.top - box.top;
      else if (box.bottom > dialogBox.bottom) this.dialog.scrollTop += box.bottom - dialogBox.bottom;
    }

    syncChallengeVisibility() {
      if (!this.formsRoot || !this.dialog.open) return false;
      let visible;
      try { visible = this.externalChallengeVisible(); }
      catch { this.dialog.setAttribute('aria-modal', 'false'); return true; }
      const wasVisible = this.challengeVisible;
      this.challengeVisible = visible;
      this.dialog.setAttribute('aria-modal', visible ? 'false' : 'true');
      const active = this.activePublicElement();
      if (!visible && wasVisible && !this.insideDialog(active) && !document.hidden) this.focusFormsElement(this.formsFocusable()[0]);
      return visible;
    }

    handleFormsKeydown(event) {
      if (!this.dialog.open || event.defaultPrevented || event.isComposing || event.altKey || event.ctrlKey || event.metaKey ||
          (event.key !== 'Tab' && event.key !== 'Escape') || this.syncChallengeVisibility()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      const controls = this.formsFocusable();
      const active = this.activePublicElement();
      const index = controls.indexOf(active);
      if (index < 0 || (event.shiftKey && index === 0) || (!event.shiftKey && index === controls.length - 1)) {
        event.preventDefault();
        this.focusFormsElement(controls[event.shiftKey ? controls.length - 1 : 0]);
      }
    }

    afterDialogClosed() {
      clearTimeout(this.formsTimer);
      this.challengeObserver?.disconnect();
      document.documentElement.classList.remove('sh-capture-is-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (!this.formsRoot) return;
      this.clearFormsIdentity();
      if (this.backdrop) this.backdrop.hidden = true;
      this.dialog.removeAttribute('aria-modal');
      this.challengeVisible = false;
      try {
        const target = [this.returnFocus, this.trigger, document.querySelector('main[tabindex], [role="main"][tabindex]')]
          .find((element) => element && !this.insideDialog(element) && this.elementVisible(element));
        target?.focus({ preventScroll: true });
      } catch { /* A removed or unavailable focus target never prevents closing. */ }
      this.returnFocus = null;
    }

    formsElementVisible(element) {
      // Cross open shadow hosts using standard DOM only; never inspect Forms classes or state.
      return this.dialog.open && this.elementVisible(element, this.dialog.getBoundingClientRect());
    }

    formsSuccessVisible() {
      if (!this.formsRoot || !this.dialog.open) return false;
      const expected = this.formsRoot.dataset.successHeading;
      if (!expected) return false;
      const roots = [this.formsRoot];
      for (let index = 0; index < roots.length; index++) {
        for (const element of roots[index].querySelectorAll('*')) {
          if (element.shadowRoot) roots.push(element.shadowRoot);
        }
      }
      // A submit attempt, a hidden success template, or a heading outside this app block is insufficient.
      if (roots.some((root) => Array.from(root.querySelectorAll('form')).some((form) =>
        this.elementVisible(form) || Array.from(form.querySelectorAll('input, select, textarea, button')).some((control) => this.elementVisible(control))))) return false;
      if (roots.some((root) => Array.from(root.querySelectorAll('input, select, textarea')).some((control) => this.elementVisible(control)))) return false;
      return roots.some((root) => Array.from(root.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]')).some((heading) =>
        heading.textContent?.trim() === expected && this.formsElementVisible(heading)));
    }

    watchFormsSuccess() {
      clearTimeout(this.formsTimer);
      if (!this.formsRoot || !this.dialog.open || document.hidden || this.dataset.state === 'success') return;
      this.reportClarityView();
      try {
        if (!this.syncChallengeVisibility() && this.formsSuccessVisible()) {
          const age = Date.now() - this.formsEmailAt;
          const email = this.formsEmailCommitted && age >= 0 && age <= 600000 ? this.formsEmail : '';
          if (email) {
            try {
              const adapter = window.SportsHubEmailEvents;
              const identify = adapter?.identifyAfterSignup;
              if (typeof identify === 'function' && this.visitorConsentAllowed()) identify.call(adapter, email);
            } catch { /* An optional email provider cannot block native signup success. */ }
          }
          this.clearFormsEmail();
          this.formsSuccessHadAttempt = Boolean(email);
          this.dataset.state = 'success';
          this.rememberCompletion();
          this.reportClarityEvent('sh_capture_success');
          this.querySelector('[data-capture-success]')?.focus();
          if (email) this.prepareVisitorIdentification(true, email);
          else this.dataset.visitorIdentification = 'unavailable';
          return;
        }
      } catch { /* If Forms changes or cannot be inspected, its UI and the close button keep working. */ }
      // Observe only while this dialog is visible; no submission interception or private API is used.
      this.formsTimer = setTimeout(() => this.watchFormsSuccess(), 500);
    }

    clearVisitorBridge() {
      if (!this.visitorBridgeKey) return;
      try { sessionStorage.removeItem(this.visitorBridgeKey); }
      catch { /* Unavailable storage never blocks a native subscription. */ }
    }

    takeVisitorBridge() {
      try {
        const raw = sessionStorage.getItem(this.visitorBridgeKey);
        sessionStorage.removeItem(this.visitorBridgeKey);
        // A customer POST can mark several forms successful; only this form's native anchor qualifies.
        if (window.location.hash !== '#' + this.form.id) return null;
        const saved = JSON.parse(raw || 'null');
        const age = Date.now() - saved?.at;
        if (!saved || typeof saved !== 'object' || Array.isArray(saved) ||
            typeof saved.formId !== 'string' || !saved.formId || saved.formId !== this.form.id ||
            typeof saved.at !== 'number' || !Number.isFinite(saved.at) || age < 0 || age > 600000 ||
            typeof saved.email !== 'string' || !/^[^\s@]+@[^\s@]+$/.test(saved.email)) return null;
        return saved.email;
      } catch { return null; }
    }

    visitorConsentAllowed() {
      let privacy;
      try { privacy = window.Shopify?.customerPrivacy; } catch { /* Unknown is never permission. */ }
      const read = (method) => {
        try { return typeof privacy?.[method] === 'function' ? String(privacy[method]() === true) : 'unknown'; }
        catch { return 'unknown'; }
      };
      const analytics = read('analyticsProcessingAllowed');
      const marketing = read('marketingAllowed');
      if (this.formsRoot) {
        this.dataset.visitorAnalyticsAllowed = analytics;
        this.dataset.visitorMarketingAllowed = marketing;
      }
      return analytics === 'true' && marketing === 'true';
    }

    rememberVisitorBridge(email) {
      if (!this.form.id || !this.visitorConsentAllowed()) {
        this.clearVisitorBridge();
        return;
      }
      // Consented, same-tab bridge across native POST/captcha only. Never localStorage.
      // Logically expires after ten minutes; success consumes/deletes it, errors or consent withdrawal clear it.
      try {
        sessionStorage.setItem(this.visitorBridgeKey, JSON.stringify({ email, formId: this.form.id, at: Date.now() }));
      } catch { /* Pre-POST Visitor API remains usable if sessionStorage is blocked. */ }
    }

    prepareVisitorIdentification(afterNativeSuccess, postSuccessEmail = '') {
      let pendingEmail = postSuccessEmail;
      let restored = false;
      let expiryTimer;
      const signal = this.controller.signal;
      const deadline = Date.now() + 5000;
      const cancel = () => {
        pendingEmail = '';
        restored = true;
        clearTimeout(expiryTimer);
        signal.removeEventListener('abort', cancel);
        if (this.cancelPendingFormsIdentification === cancel) this.cancelPendingFormsIdentification = null;
      };
      if (this.formsRoot && afterNativeSuccess && pendingEmail) {
        this.cancelPendingFormsIdentification?.();
        this.cancelPendingFormsIdentification = cancel;
        signal.addEventListener('abort', cancel, { once: true });
        expiryTimer = setTimeout(() => {
          cancel();
          this.dataset.visitorIdentification = 'unavailable';
        }, 5000);
      }
      const identifyAfterSuccess = () => {
        if (restored) return;
        if (this.formsRoot && (signal.aborted || Date.now() > deadline || (afterNativeSuccess && !this.dialog.open))) { cancel(); return; }
        const email = pendingEmail;
        cancel();
        if (this.formsRoot) {
          this.visitorConsentAllowed();
          if (!afterNativeSuccess) {
            if (this.dataset.state !== 'success') this.dataset.visitorIdentification = 'idle';
            return;
          }
        }
        if (!afterNativeSuccess) return;
        this.identifyVisitorFromForm(email, false);
      };
      try {
        const shopify = window.Shopify;
        const privacy = shopify?.customerPrivacy;
        if (typeof privacy?.analyticsProcessingAllowed === 'function' && typeof privacy?.marketingAllowed === 'function') {
          identifyAfterSuccess();
          return;
        }

        this.dataset.visitorIdentification = 'unavailable';
        if (typeof shopify?.loadFeatures !== 'function') {
          cancel();
          return;
        }
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
          if (signal.aborted || error) {
            cancel();
            if (error && !this.acceptedEmails.size) this.dataset.visitorIdentification = 'unavailable';
            return;
          }
          identifyAfterSuccess();
        });
      } catch {
        cancel();
        // A missing privacy feature must never prevent the native subscription.
        this.dataset.visitorIdentification = 'unavailable';
      }
    }

    identifyVisitorFromForm(bridgedEmail = '', preserveForPost = true) {
      if (!this.isSupportedLocale || this.dataset.review === 'true') return;
      if (this.formsRoot && (!this.formsSuccessHadAttempt || this.dataset.state !== 'success' || preserveForPost || !this.dialog.open || this.controller.signal.aborted)) return;
      try {
        const email = bridgedEmail || this.form?.querySelector('input[name="contact[email]"]')?.value?.trim();
        if (!email) {
          this.dataset.visitorIdentification = 'unavailable';
          return;
        }
        if (preserveForPost && !this.form.checkValidity()) return;
        if (!/^[^\s@]+@[^\s@]+$/.test(email)) {
          this.dataset.visitorIdentification = 'rejected';
          return;
        }

        const shopify = window.Shopify;
        const privacy = shopify?.customerPrivacy;
        if (this.formsRoot) this.visitorConsentAllowed();
        if (typeof privacy?.analyticsProcessingAllowed !== 'function' || typeof privacy?.marketingAllowed !== 'function') {
          this.clearVisitorBridge();
          this.dataset.visitorIdentification = 'unavailable';
          return;
        }
        if (privacy.analyticsProcessingAllowed() !== true || privacy.marketingAllowed() !== true) {
          this.clearVisitorBridge();
          this.dataset.visitorIdentification = 'blocked';
          return;
        }
        if (preserveForPost) this.rememberVisitorBridge(email);
        const analytics = shopify?.analytics;
        const visitor = analytics?.visitor;
        if (typeof visitor !== 'function') {
          this.dataset.visitorIdentification = 'unavailable';
          return;
        }

        const identityKey = email.toLowerCase();
        if (this.acceptedEmails.has(identityKey)) return;
        // Forms identity is optional and is sent only after success and a final literal-true consent check.
        if (this.formsRoot && !this.visitorConsentAllowed()) {
          this.dataset.visitorIdentification = this.dataset.visitorAnalyticsAllowed === 'unknown' || this.dataset.visitorMarketingAllowed === 'unknown' ? 'unavailable' : 'blocked';
          return;
        }
        const accepted = visitor.call(analytics, { email });
        if (accepted === true) this.acceptedEmails.add(identityKey);
        // A true response only accepts this identity payload; it proves no subscription or customer link.
        this.dataset.visitorIdentification = accepted === true ? 'accepted' : 'rejected';
      } catch {
        this.dataset.visitorIdentification = 'error';
      }
    }

    readPreferences() {
      try {
        const value = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
        return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      }
      catch { return {}; }
    }

    writePreferences() {
      // UI dismissal/completion preferences only. Email addresses are never stored here.
      try { localStorage.setItem(this.storageKey, JSON.stringify(this.preferences)); }
      catch { /* The form and close controls still work when storage is unavailable. */ }
    }

    dismissUntil() {
      const days = Number(this.dataset.dismissDays) || 7;
      return Date.now() + days * 86400000;
    }

    rememberClose() {
      this.preferences.autoUntil = this.dismissUntil();
      this.writePreferences();
    }

    isCompleted() {
      return this.isSupportedLocale && Boolean(this.preferences[this.completionKey]);
    }

    rememberCompletion() {
      if (!this.isSupportedLocale) return;
      this.preferences[this.completionKey] = Date.now();
      this.writePreferences();
      clearTimeout(this.timer);
      this.updateTeaser();
    }

    refreshPreferences() {
      this.preferences = { ...this.preferences, ...this.readPreferences() };
      clearTimeout(this.timer);
      this.updateTeaser();
      if (!this.dialog.open) this.schedule();
    }

    updateTeaser() {
      if (this.teaser) this.teaser.hidden = this.isCompleted() || Number(this.preferences.teaserUntil || 0) > Date.now();
    }

    busy() {
      if (this.formsRoot) {
        try { if (this.externalChallengeVisible()) return true; }
        catch { /* An unavailable challenge check does not block the normal busy checks. */ }
      }
      const candidates = document.querySelectorAll('dialog[open], [aria-modal="true"], #shopify-pc__banner, .shopify-pc__banner, cart-drawer.active');
      return Array.from(candidates).some((element) => {
        if (element === this.dialog || this.contains(element)) return false;
        // The search modal can retain a 2px border inside closed <details>.
        // It is not an open modal and must not postpone the Spanish signup.
        if (this.isSupportedLocale) {
          for (let ancestor = element; ancestor; ancestor = ancestor.parentElement) {
            if (ancestor.hidden || (ancestor.tagName === 'DETAILS' && ancestor.getAttribute('open') === null)) return false;
          }
        }
        const style = getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getBoundingClientRect().height > 0;
      });
    }

    schedule() {
      if (this.isCompleted() || this.autoShown || this.dataset.auto !== 'true' || document.hidden || Number(this.preferences.autoUntil || 0) > Date.now()) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const seconds = Number(mobile ? this.dataset.delayMobile : this.dataset.delayDesktop) || 8;
      this.timer = setTimeout(() => this.tryAutomaticOpen(), seconds * 1000);
    }

    tryAutomaticOpen() {
      if (this.isCompleted() || document.hidden || this.autoShown) return;
      if (this.busy()) {
        if (++this.attempts < 30) this.timer = setTimeout(() => this.tryAutomaticOpen(), 1000);
        return;
      }
      this.autoShown = true;
      this.open();
      this.rememberClose();
    }

    reportClarityEvent(name) {
      try {
        if (!['sh_capture_view', 'sh_capture_close', 'sh_capture_success'].includes(name) ||
            this.dataset.review === 'true' || !this.isSupportedLocale || !this.dialog?.open ||
            document.hidden || !this.elementVisible(this.dialog)) return false;
        if (!this.visitorConsentAllowed()) {
          this.dataset.clarityStatus = 'blocked';
          return false;
        }
        if (typeof window.clarity !== 'function') {
          this.dataset.clarityStatus = 'unavailable';
          return false;
        }
        window.clarity('set', 'sh_capture_environment', this.dataset.clarityEnvironment === 'production' ? 'production' : 'qa');
        window.clarity('set', 'sh_capture_locale', this.dataset.locale);
        window.clarity('set', 'sh_capture_seen', 'true');
        window.clarity('event', name);
        this.dataset.clarityStatus = 'dispatched';
        this.dataset.clarityLastEvent = name;
        return true;
      } catch {
        this.dataset.clarityStatus = 'error';
        return false;
      }
    }

    reportClarityView() {
      if (this.clarityViewReported || this.dataset.state === 'success') return;
      this.clarityViewReported = this.reportClarityEvent('sh_capture_view');
    }

    reportClarityClose() {
      if (!this.dialog?.open || this.dataset.state === 'success' || this.clarityCloseReported) return;
      this.reportClarityView();
      this.clarityCloseReported = this.reportClarityEvent('sh_capture_close');
    }

    open() {
      clearTimeout(this.timer);
      if (this.dialog.open) return;
      if (this.formsRoot) {
        try { if (this.externalChallengeVisible()) return; }
        catch { /* The close button remains available if challenge detection is unavailable. */ }
        this.returnFocus = this.activePublicElement();
        // Non-modal HTML dialog deliberately leaves hCaptcha's external challenge interactive.
        try {
          if (typeof this.dialog.show === 'function') this.dialog.show();
          else this.dialog.setAttribute('open', '');
        } catch { this.dialog.setAttribute('open', ''); }
        if (this.backdrop) this.backdrop.hidden = false;
        if (!this.syncChallengeVisibility()) this.focusFormsElement(this.formsFocusable()[0]);
        try {
          this.challengeObserver = new MutationObserver(() => this.syncChallengeVisibility());
          this.challengeObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] });
        } catch { /* Event checks and the existing open-dialog polling still handle challenge visibility. */ }
      } else {
        if (typeof this.dialog.showModal !== 'function') return;
        this.dialog.showModal();
      }
      document.documentElement.classList.add('sh-capture-is-open');
      this.clarityViewReported = false;
      this.clarityCloseReported = false;
      this.trigger?.setAttribute('aria-expanded', 'true');
      if (this.dataset.state === 'success') this.querySelector('[data-capture-success]')?.focus();
      this.reportClarityView();
      if (this.formsRoot) this.watchFormsSuccess();
    }

    close() {
      if (this.formsRoot && this.dialog.open) {
        try { if (this.externalChallengeVisible()) return; }
        catch { /* A failed visibility check must not disable the visible close button. */ }
        this.clearFormsIdentity();
      }
      this.reportClarityClose();
      this.rememberClose();
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.afterDialogClosed();
      }
    }
  }

  customElements.define('sh-email-capture', SportsHubEmailCapture);
})();

